/**
 * FAQ Service
 * Provides API integration for FAQ management operations
 */

import apiClient from './apiClient';

const FAQ_BASE_URL = '/faqs';

/**
 * FAQ Category API Methods
 */
export const faqCategoryService = {
  /**
   * Get all FAQ categories with optional filters
   * @param {Object} params - Query parameters
   * @param {string} [params.module_type] - Filter by module type (admin, patient, doctor, clinician)
   * @param {boolean} [params.is_active] - Filter by active status
   * @param {number} [params.page] - Page number (default: 1)
   * @param {number} [params.per_page] - Items per page (default: 50)
   * @returns {Promise<Object>} - Paginated categories list
   */
  getAll: async (params = {}) => {
    const response = await apiClient.get(`${FAQ_BASE_URL}/categories`, { params });
    return response.data;
  },

  /**
   * Get categories for a specific module
   * @param {string} moduleType - Module type (admin, patient, doctor, clinician)
   * @returns {Promise<Array>} - Categories for the module
   */
  getByModule: async (moduleType) => {
    const response = await apiClient.get(`${FAQ_BASE_URL}/categories`, {
      params: { module_type: moduleType, is_active: true }
    });
    return response.data.items || [];
  },

  /**
   * Get a specific category by ID
   * @param {number} categoryId - Category ID
   * @returns {Promise<Object>} - Category details
   */
  getById: async (categoryId) => {
    const response = await apiClient.get(`${FAQ_BASE_URL}/categories/${categoryId}`);
    return response.data;
  },

  /**
   * Create a new FAQ category (admin only)
   * @param {Object} categoryData - Category data
   * @param {string} categoryData.name - Category name
   * @param {string} categoryData.module_type - Module type
   * @param {string} [categoryData.description] - Category description
   * @param {number} [categoryData.display_order] - Display order
   * @returns {Promise<Object>} - Created category
   */
  create: async (categoryData) => {
    const response = await apiClient.post(`${FAQ_BASE_URL}/categories`, categoryData);
    return response.data;
  },

  /**
   * Update an FAQ category (admin only)
   * @param {number} categoryId - Category ID
   * @param {Object} categoryData - Updated category data
   * @returns {Promise<Object>} - Updated category
   */
  update: async (categoryId, categoryData) => {
    const response = await apiClient.put(`${FAQ_BASE_URL}/categories/${categoryId}`, categoryData);
    return response.data;
  },

  /**
   * Delete an FAQ category (admin only)
   * @param {number} categoryId - Category ID
   * @param {boolean} [permanent=false] - Permanently delete (vs soft delete)
   * @returns {Promise<void>}
   */
  delete: async (categoryId, permanent = false) => {
    await apiClient.delete(`${FAQ_BASE_URL}/categories/${categoryId}`, {
      params: { permanent }
    });
  }
};

/**
 * FAQ API Methods
 */
export const faqService = {
  /**
   * Get all FAQs with optional filters
   * @param {Object} params - Query parameters
   * @param {string} [params.module_type] - Filter by module type
   * @param {number} [params.category_id] - Filter by category ID
   * @param {boolean} [params.is_active] - Filter by active status
   * @param {boolean} [params.is_featured] - Filter by featured status
   * @param {string} [params.search] - Search in question and answer
   * @param {number} [params.page] - Page number (default: 1)
   * @param {number} [params.per_page] - Items per page (default: 50)
   * @returns {Promise<Object>} - Paginated FAQs list
   */
  getAll: async (params = {}) => {
    const response = await apiClient.get(`${FAQ_BASE_URL}/`, { params });
    return response.data;
  },

  /**
   * Get FAQs for a specific module with categories
   * @param {string} moduleType - Module type (admin, patient, doctor, clinician)
   * @param {Object} [options] - Additional options
   * @param {boolean} [options.featured_only] - Get only featured FAQs
   * @param {number} [options.limit] - Limit number of FAQs
   * @returns {Promise<Object>} - Module FAQs with categories
   */
  getByModule: async (moduleType, options = {}) => {
    const response = await apiClient.get(`${FAQ_BASE_URL}/module/${moduleType}`, {
      params: options
    });
    return response.data;
  },

  /**
   * Get a specific FAQ by ID
   * @param {number} faqId - FAQ ID
   * @returns {Promise<Object>} - FAQ details
   */
  getById: async (faqId) => {
    const response = await apiClient.get(`${FAQ_BASE_URL}/${faqId}`);
    return response.data;
  },

  /**
   * Create a new FAQ (admin only)
   * @param {Object} faqData - FAQ data
   * @param {string} faqData.question - FAQ question
   * @param {string} faqData.answer - FAQ answer
   * @param {string} faqData.module_type - Module type
   * @param {number} [faqData.category_id] - Category ID
   * @param {boolean} [faqData.is_featured] - Featured status
   * @param {number} [faqData.display_order] - Display order
   * @returns {Promise<Object>} - Created FAQ
   */
  create: async (faqData) => {
    const response = await apiClient.post(`${FAQ_BASE_URL}/`, faqData);
    return response.data;
  },

  /**
   * Update an FAQ (admin only)
   * @param {number} faqId - FAQ ID
   * @param {Object} faqData - Updated FAQ data
   * @returns {Promise<Object>} - Updated FAQ
   */
  update: async (faqId, faqData) => {
    const response = await apiClient.put(`${FAQ_BASE_URL}/${faqId}`, faqData);
    return response.data;
  },

  /**
   * Delete an FAQ (admin only)
   * @param {number} faqId - FAQ ID
   * @param {boolean} [permanent=false] - Permanently delete (vs soft delete)
   * @returns {Promise<void>}
   */
  delete: async (faqId, permanent = false) => {
    await apiClient.delete(`${FAQ_BASE_URL}/${faqId}`, {
      params: { permanent }
    });
  },

  /**
   * Bulk update FAQ display order (admin only)
   * @param {Array<Object>} orderUpdates - Array of {id, display_order}
   * @returns {Promise<Object>} - Bulk update result
   */
  bulkUpdateOrder: async (orderUpdates) => {
    const response = await apiClient.post(`${FAQ_BASE_URL}/bulk-update-order`, orderUpdates);
    return response.data;
  },

  /**
   * Get FAQ statistics (admin only)
   * @returns {Promise<Object>} - FAQ statistics
   */
  getStats: async () => {
    const response = await apiClient.get(`${FAQ_BASE_URL}/stats/overview`);
    return response.data;
  }
};

// Default export with both services
export default {
  faq: faqService,
  category: faqCategoryService
};
